import { useEffect, useState } from 'react';
import {Row, Col, Card, Button, Collapse, Divider, Space, Tag, Typography, Tabs } from 'antd';
import { SUBSCRIPTION_STATUS } from '../../../constants/AppConstants';
import {
  convertCentsIntoDollar,
  formatDate, isSupportAgent2, isSupportLead
} from '../../../helpers/genericHelper';
import CustomText from '../../CustomText';
import LoaderComponent from '../../Loader';
import { PersistedCollapse, PersistedTabs } from '../../PersistedSections';
import { useParams } from 'react-router';
import API from '../../../services/API';
import PreviewInvoiceComponent from "./Subscription/PreviewInvoiceComponent";
import UpdateSubscriptionModal from "./Subscription/UpdateSubscriptionModal";
import SubscriptionRenewModal from './Subscription/SubscriptionRenewModal';
import SubscriptionResumeModal from './Subscription/SubscriptionResumeModal';
import SubscriptionCancelModal from './Subscription/SubscriptionCancelModal';
import SubscriptionDiscount from './Subscription/SubscriptionDiscount';
import InvoiceDetails from './Subscription/InvoiceDetails';
import Data from '../../CustomText/data';
import SubscriptionPlans from './Subscription/SubscriptionPlans';
import AvaAssist from './Subscription/AvaAssist';
import PaymentIssues from './Subscription/PaymentIssues';

const { Panel } = Collapse;
const { TabPane } = Tabs;

function Subscriptions({ subscriptions, loading, refreshList, charges, disputes, invoices }) {
  const [modalVisible, setModalVisible] = useState(false);
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState(null);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedSubscriptionData, setSelectedSubscriptionData] = useState(null);
  const [modalLoading, showModalLoading] = useState(false);
  const [isRenewSubscriptionAllowed, setIsRenewSubscriptionAllowed] = useState(false);
  const [stripeBlocked, setStripeBlocked] = useState(null);
  const [winback, setWinback] = useState(null);

  let { userId } = useParams();

  const getBlockHistory = async () => {
    let res = await API.getBlockHistory(userId);
    setStripeBlocked(res?.blockHistory?.map((block) => block.blockCode === "STRIPE_DISPUTE"));
  }

  const getSubscriptionData = async () => {
    const subscriptionStatuses = subscriptions
      .map((subscription) => subscription.status)
      .filter((status) => [
        SUBSCRIPTION_STATUS.ACTIVE,
        SUBSCRIPTION_STATUS.PAST_DUE,
        SUBSCRIPTION_STATUS.WILL_CANCEL,
        SUBSCRIPTION_STATUS.INITIALIZING
      ].includes(status));

      setIsRenewSubscriptionAllowed(subscriptionStatuses.length === 0);
  }

  const winBackData = async () => {
    const winbackOffer = await API.getSubscriptionWinback({ userId })
    setWinback(winbackOffer);
  }

  useEffect(() => {
    getBlockHistory();
    winBackData();
  }, [userId])

  useEffect(() => {
    if (!subscriptions) return;
    getSubscriptionData();
  }, [subscriptions]);

  const closeModal = () => {
    setModalVisible(false);
  }

  const showCloseSubscriptionModal = (data) => {
    setCurrentSubscriptionId(data?.id)
    setModalVisible(true);
  }

  const showUpdateSubscriptionModal = (data) => {
    setSelectedSubscriptionData(data);
    setCurrentSubscriptionId(data?.id);
    setUpdateModalVisible(true);
  };

  return (
    <>
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          {(subscriptions && subscriptions.length > 0) ?
            subscriptions.map((data) => {
              const filteredInvoices = invoices
                .filter((i) => i.subscription === data?.stripeSubscriptionId);

              return (
                <Card bordered={true}
                  hoverable={true}
                  className="criclebox tablespace mb-16 bottom-bordered-card"
                  style={{ paddingTop: 25 }}
                  key={data.stripeSubscriptionId}
                  extra={<>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Row>
                        <Col span={24}>
                          <Tag color={data?.status === 'ACTIVE' ? "success" : "error"}>
                            <small>Subscription Status:</small> <strong>{data?.status}</strong>
                          </Tag>
                          <Tag >
                            <small>Subscription Plan:</small> <strong>{data?.interval}</strong>
                          </Tag>
                        </Col>
                      </Row>
                      {' '}
                      <Row>
                        <Col span={24}>
                          {
                            isSupportAgent2() && (data.status !== SUBSCRIPTION_STATUS.CANCELLED ||
                              !data.subscriptionCancellationTs) && data.status !== SUBSCRIPTION_STATUS.INITIALIZING_FAILED &&
                              <Button
                                  type={'danger'}
                                  onClick={() => showCloseSubscriptionModal(data)}
                                  className="mr-5"
                                  >
                                {'Cancel'}
                              </Button>
                          }

                          {
                            isSupportLead() && (data.status !== SUBSCRIPTION_STATUS.CANCELLED ||
                              !data.subscriptionCancellationTs) && data.status !== SUBSCRIPTION_STATUS.INITIALIZING_FAILED &&
                              <Button
                                  type={'primary'}
                                  onClick={() => showUpdateSubscriptionModal(data)}
                                  className="mr-5"
                                  >
                                {'Update'}
                              </Button>
                          }
                          
                          {
                            isSupportAgent2() && data.status === SUBSCRIPTION_STATUS.WILL_CANCEL &&
                              <SubscriptionResumeModal refreshList={refreshList} userId={userId} subscriptionId={data?.id} />
                          }

                          <SubscriptionPlans userId={userId} refreshList={refreshList} />
                          
                        </Col>
                      </Row>
                    </Space>
                  </>}
                  >

                  <Row
                    className="account-details-box-container"
                    gutter={[30, 30]}
                    style={{ width: '92%' }}
                  >
                    
                    <Col span="6">
                      <Data title="Stripe Subscription Id" value={data?.stripeSubscriptionId} />
                      {
                        data?.stripeSubscriptionId &&
                          <a
                            href={"https://dashboard.stripe.com/subscriptions/" + data?.stripeSubscriptionId}
                            title="Open in Stripe"
                            target="_blank"
                          ><strong>&nbsp;<small>open</small></strong></a>
                      }
                    </Col>
                    <Col span="6">
                      <Data title="Subscription Sign Up TS" value={data?.subscriptionSignUpTs ? formatDate(data?.subscriptionSignUpTs, true) : null} />
                    </Col>
                    
                    <Col span="6">
                      <Data title="Current Period End" value={data?.currentPeriodEndTs ? formatDate(data?.currentPeriodEndTs, true) : null} />
                    </Col>
                    
                    <Col span="6">
                      <Data title="Subscription Cancellation" value={data?.subsCancellationTs ? formatDate(data?.subsCancellationTs, true) : null} />
                    </Col>

                    <Col span="6">
                      <Data title="Subscription Automatic Cancellation" value={data?.pendingCancellationTs ? formatDate(data?.pendingCancellationTs, true) : null} />
                    </Col>

                    <Col span="6">
                      <Data title="Price per Interval" value={data?.priceInCents ? convertCentsIntoDollar(data?.priceInCents) : null} />
                    </Col>
                    
                    
                    <Col span="6">
                      <Data title="Undiscounted Price" value={data?.undiscountedPriceInCents ? convertCentsIntoDollar(data?.undiscountedPriceInCents) : null} />
                    </Col>
                    
                    <Col span="6">
                      <Data title="Renewal Price" value={data?.renewalPriceInCents ? convertCentsIntoDollar(data?.renewalPriceInCents) : null} />
                    </Col>
                    
                    <Col span="6">
                      <Data title="Amount Past Due" value={data?.amountPastDueInCents ? convertCentsIntoDollar(data?.amountPastDueInCents) : null} />
                    </Col>
                    
                    <Col span="6">
                      <Data title={<>Free Trial <small>(ended date)</small> </>} value={
                        data?.freeTrial && data?.freeTrialEndTs
                          ? formatDate(data?.freeTrialEndTs, true)
                          : "- No Free Trial -"
                        } />
                    </Col>
                    
                    
                    <Col span="6">
                      <Data title="Coupon (End) Date" value={data?.couponEndTs ? formatDate(data?.couponEndTs, true) : null} />
                    </Col>
                    
                  </Row>
                  <Row>
                    <Col span="18" offset={3}>
                      <PersistedTabs name={`subscription-tab:${data.stripeSubscriptionId}`} defaultActiveKey="1">
                        <TabPane tab="Cancellation" key="1" style={{ minHeight: '200px' }}>
                          {(data.status === SUBSCRIPTION_STATUS.CANCELLED ||
                            !!data.subscriptionCancellationTs) && (
                              <Card
                                bordered={true}
                                className="criclebox tablespace mb-16 card-cancel-subscription"
                              >
                                <h4 style={{ color: 'red' }}>Cancelled</h4>
                                <Row
                                  className="subscription-cancelled-card"
                                  gutter={[30, 30]}
                                >
                                  <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                                    <h4>Canceled by</h4>
                                    <CustomText
                                      dataSet={data}
                                      property1={'cancelledBy'}
                                    />
                                  </Col>
                                  <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                                    <h4>Cancelation TS</h4>
                                    <CustomText
                                      dataSet={data}
                                      property1={'subscriptionCancellationTs'}
                                      formatter={formatDate}
                                    />
                                  </Col>
                                  <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                                    <h4>Paid early cancelation fee</h4>
                                    <CustomText
                                      dataSet={data}
                                      property1={'subscriptionCancellationFee'}
                                      formatter={convertCentsIntoDollar}
                                    />
                                  </Col>
                                  <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                                    <h4>Cancelation reason</h4>
                                    <CustomText
                                      dataSet={data}
                                      property1={'cancelReason'}
                                    />
                                  </Col>
                                </Row>
                              </Card>
                            )}
                            { data?.status !== SUBSCRIPTION_STATUS.CANCELLED
                              && !data?.subscriptionCancellationTs
                              && <Typography.Title level={5}>Not Cancelled!</Typography.Title>}
                        </TabPane>
                        <TabPane tab="Invoices" key="2" style={{ minHeight: '200px' }}>
                          <Space direction="vertical" style={{ width: '100%', margin: '20px' }}>
                            <Row>
                              { filteredInvoices.length ?
                                  filteredInvoices.map((invoice) => <Col span="24" key={invoice.id}>
                                    {
                                      <InvoiceDetails
                                        invoice={invoice}
                                        charges={charges?.filter((charge) => invoice.id === charge?.invoice)}
                                        disputes={disputes} />
                                    }
                                  </Col>
                                ) : <strong>No Invoices found for this subscriptions!</strong>
                              }
                            </Row>
                          </Space>
                        </TabPane>
                        <TabPane tab="Discount" key="3" style={{ minHeight: '200px' }}>
                          <Space direction="vertical" style={{ width: '100%', margin: '20px' }}>
                            <Row>
                              <Col span="12">
                                {
                                  <SubscriptionDiscount discount={data?.subscriptionDiscount} />
                                }
                              </Col>
                            </Row>
                          </Space>
                        </TabPane>
                        <TabPane tab="Winback" key="4" style={{ minHeight: '200px' }} disabled={!winback}>
                          {winback?.copy && <Space direction="vertical" style={{ width: '100%', margin: '20px' }}>
                            <Row>
                              <Col span="12">
                                <Typography.Title level={3}>{winback.copy.title}</Typography.Title>
                                <Typography.Text>{winback.copy.description}</Typography.Text>
                              </Col>
                              <Col span="12">
                              {
                                winback.plans.length > 0 && <>
                                  <Typography.Title level={5}>Available Plans</Typography.Title>
                                  <ol>
                                    {winback.plans.map((plan) => <li key={plan.id}>
                                      <Row>
                                        <Col span="24">
                                          <Typography.Title level={5}><Typography.Text type="danger" delete>{plan.originalPaymentTermText}</Typography.Text> {plan.billedText}</Typography.Title>
                                          <Typography.Text>{plan.description}</Typography.Text>
                                        </Col>
                                      </Row>
                                    </li>)}
                                  </ol>
                                </>
                              }
                              </Col>
                            </Row>
                          </Space>}
                        </TabPane>
                        <TabPane tab="Ava Assist" key="5"><AvaAssist userId={userId}/></TabPane>
                        <TabPane tab="Payment Issues" key="6" style={{ minHeight: '200px' }}>
                          <PaymentIssues userId={userId}/>
                        </TabPane>
                      </PersistedTabs>
                    </Col>
                  </Row>
                  {(data.status !== SUBSCRIPTION_STATUS.CANCELLED && data.status !== SUBSCRIPTION_STATUS.INITIALIZING_FAILED)
                      && (
                          <Row style={{ marginLeft: 20, marginBottom: 20 }}>
                            <Col xs={24} xl={24} sm={24} md={24}>
                              <PersistedCollapse name={`subscription-sections:${data.stripeSubscriptionId}`}>
                                <Panel header="Preview Upcoming Invoice" key="5">
                                  <PreviewInvoiceComponent userId={userId} subscriptionId={data.id} />
                                </Panel>
                              </PersistedCollapse>
                            </Col>
                          </Row>
                      )}
                </Card>
              );
            }) :
            <Row className="no-data-card">No Data Found</Row>}

        </>
      )}

      { isSupportLead() && isRenewSubscriptionAllowed && <SubscriptionRenewModal refreshList={refreshList} userId={userId} /> }

      <SubscriptionCancelModal
        modalVisible={modalVisible}
        closeModal={closeModal}
        userId={userId}
        currentSubscriptionId={currentSubscriptionId}
        refreshList={refreshList}
        setModalVisible={setModalVisible}
        modalLoading={modalLoading}
        showModalLoading={showModalLoading} />

      {updateModalVisible && (
          <UpdateSubscriptionModal
              userId={userId}
              activeSubscriptionId={currentSubscriptionId}
              onClose={() => setUpdateModalVisible(false)}
              refreshList={refreshList}
          />
      )}

    </>

  );
}

export default Subscriptions;
